<template>
  <div>
    <modal-provider
      name="user-type-modal"
      :width="500"
      @before-open="beforeOpen"
      @before-close="beforeClose"
      :title="$t('select_user_type')"
    >
      <div class="user-type-modal">
        <template v-if="isChangeUserType">
          <div class="user-type-modal__item">
            <UserType />
          </div>
        </template>
        <template v-if="isManager && canManagerSelectCity">
          <div class="user-type-modal__item">
            <h2 class="user-type-modal__title">{{ $t('select_your_city') }}</h2>
            <GeoSelectCity user-type-modal type="select" />
            <span class="error-message">{{ errors.city }}</span>
          </div>
          <div class="user-type-modal__item">
            <h2 class="user-type-modal__title">
              {{ $t('select_your_store') }}
            </h2>
            <UserSelectShop :is-change-user-type="isChangeUserType" />
            <span class="error-message">{{ errors.store }}</span>
          </div>
        </template>
        <div class="user-type-modal__btns-wrap" v-if="isChangeUserType">
          <Button v-if="isManager"
                  @click.native="hideModalAndResetUserType"
                  type="gray"
                  class="user-type-modal__btn">
            {{ $t('user_type_reset') }}
          </Button>
          <Button
            @click.native="hideModalAndApproveUserType"
            class="user-type-modal__btn user-type-modal__btn--approve"
          >
            {{ $t('user_type_approve') }}
          </Button>
        </div>
      </div>
    </modal-provider>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import GeoSelectCity from '@/components/user-geo/GeoSelectCity'
import UserType from '@/components/header/user-type/UserType'
import UserSelectShop from '@/components/header/user-type/UserSelectShop'
import Button from '@/components/inputs/Button'

export default {
  name: 'UserTypeModal',
  data () {
    return {
      required: {
        city: false,
        store: false
      },
      errors: {
        city: '',
        store: ''
      },
      isChangeUserType: false,
      redirect: () => null
    }
  },
  components: {
    Button,
    UserSelectShop,
    UserType,
    GeoSelectCity
  },
  computed: {
    ...mapGetters(['urlLanguagePrefix', 'userGeoFromCookies', 'isManager', 'canManagerSelectCity']),
    ...mapState({
      selectedUserLocation: state => state.geoLocation.selectedUserLocation,
      exactUserLocation: state => state.geoLocation.exactUserLocation,
      popularCities: state => state.geoLocation.popularCities,
      managerStore: state => state.checkout.managerStore,
      managerStoreUid: state => state.checkout.managerStoreUid,
      managerType: state => state.user.managerType
    }),
    computedManagerStore () {
      if (
        this.managerStoreUid &&
        this.managerStore?.uid === this.managerStoreUid &&
        this.managerStore?.city.id === this.selectedUserLocation.city_id
      ) {
        return this.managerStore
      }

      return null
    }
  },
  methods: {
    ...mapActions(['FETCH_DELIVERY_CITIES', 'SEND_USER_CITY', 'SET_SELECTED_USER_GEO_ACTION', 'CHANGE_USER_TYPE']),
    beforeOpen (evt) {
      if (!this.popularCities) {
        // eslint-disable-next-line no-return-assign
        this.FETCH_DELIVERY_CITIES().then(response => this.$store.commit('SET_POPULAR_CITIES', response.cities))
      }
      this.isChangeUserType = evt.params && evt.params.isChangeUserType ? evt.params.isChangeUserType : false
      this.redirect = evt.params && evt.params.redirect ? evt.params.redirect : () => null
    },
    beforeClose () {
      if (this.isChangeUserType) {
        this.hideModalAndResetUserType()
      }
    },
    hideModalAndResetUserType () {
      this.CHANGE_USER_TYPE(this.$constants.USER_TYPE.CUSTOMER).then(() => {
        this.$modal.hide('user-type-modal')
        this.redirect()
      })
    },
    hideModalAndApproveUserType () {
      if (!this.isManager) {
        this.$modal.hide('user-type-modal')
        this.redirect()
      } else if (this.validateUserCityAndStore()) {
        this.$modal.hide('user-type-modal')
        this.redirect()
      }
    },
    validateUserCityAndStore () {
      if (!this.selectedUserLocation && this.required.city) {
        this.errors.city = this.$t('select_user_city_error')
        return false
      } else if (!this.computedManagerStore && this.required.store) {
        this.errors.store = this.$t('select_user_store_error')
        return false
      }

      return true
    }
  },
  watch: {
    selectedUserLocation (value) {
      if (value) this.errors.city = ''
    },
    computedManagerStore (value) {
      if (value) this.errors.store = ''
    },
    canSelectCityAndStore: {
      immediate: true,
      handler (value) {
        this.required.city = value
        this.required.store = value
      }
    }
  }
}
</script>

<style lang="sass">

[data-modal="user-type-modal"]
  .modal-body
    padding-bottom: 25px
    padding-top: 0

    .geo-modal__select
      width: 100%

.user-type-modal__title
  color: $text-color
  font-size: 18px
  font-weight: 500
  margin: 0 0 10px

.user-type-modal .user-type-modal__item
  margin-bottom: 25px

.user-type-modal__btns-wrap
  display: flex
  flex-flow: row wrap
  justify-content: center
  margin-top: 35px

  .user-type-modal__btn
    margin: 5px 8px

.user-type-modal__subtitle
  color: $placeholder-color

.user-type-modal__city-list
  +list-unstyled
  padding: 0
  margin-top: 30px
  display: flex
  justify-content: space-between
  flex-wrap: wrap

.user-type-modal__city-list-item
  list-style: none
  width: 50%

  +mmin($small)
    width: 33%

.user-type-modal__city-list-item:not(:last-child)
  margin-bottom: 10px

.user-type-modal__city-list-btn
  border: 0
  background: transparent
  color: $text-color
  padding: 5px 15px
  border-radius: 3px
  text-align: left
  font-family: 'Cera Pro', Roboto, Helvetica, Arial, sans-serif

  &--selected
    background: $nd-disabled-background-color

  &:hover
    color: $main-color

.user-type-modal__detect-geo
  display: flex
  align-items: center
  margin: 30px 0 20px 20px

  .link
    background-color: transparent
    margin-left: 10px
</style>
